/**
 * Language-neutral message descriptors.
 *
 * The coach, the event log and the game server can all describe *what* happened
 * without knowing *which language* the reader uses: they emit a key plus typed
 * parameters, and this package turns that into a sentence. Every descriptor is
 * JSON-safe, so it can cross a WebSocket or a replay file unchanged.
 */

import type { GroupKind, PlayerId, TileKind } from "@kikik/mahjong-core";

export type ClaimLabelKind = "chow" | "pong" | "kong" | "ron" | "pass";

export type MessageParam =
  | string
  | number
  | { $: "tile"; kind: TileKind }
  | { $: "tiles"; kinds: TileKind[] }
  | { $: "meld"; kind: GroupKind }
  | { $: "claim"; kind: ClaimLabelKind }
  | { $: "player"; id: PlayerId }
  | { $: "msg"; key: string; params?: Record<string, MessageParam> };

export interface Message {
  key: string;
  params?: Record<string, MessageParam>;
}

export function msg(key: string, params?: Record<string, MessageParam>): Message {
  return params ? { key, params } : { key };
}

export function tileParam(kind: TileKind): MessageParam {
  return { $: "tile", kind };
}

export function tilesParam(kinds: readonly TileKind[]): MessageParam {
  return { $: "tiles", kinds: [...kinds] };
}

export function meldParam(kind: GroupKind): MessageParam {
  return { $: "meld", kind };
}

export function claimParam(kind: ClaimLabelKind): MessageParam {
  return { $: "claim", kind };
}

export function playerParam(id: PlayerId): MessageParam {
  return { $: "player", id };
}

export function msgParam(message: Message): MessageParam {
  return message.params
    ? { $: "msg", key: message.key, params: message.params }
    : { $: "msg", key: message.key };
}
